import type { SogralSummary, SogralDestination, SogralDeparture, SogralSearchParams, SogralApiDeparture, SogralRouteStop } from '../types/sogral';

const API_BASE = import.meta.env.VITE_SOGRAL_API_BASE || '/api/sogral';
const SCRAPE_BASE = '/scrape/sogral';

// ---------- localStorage persistence helpers ----------

function lsSet(key: string, data: unknown) {
  try { localStorage.setItem(key, JSON.stringify({ data, savedAt: Date.now() })); } catch { /* quota */ }
}

function lsGet<T>(key: string): { data: T; savedAt: number } | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

// ---------- JSON API with cache ----------

const departureCache = new Map<number, { data: SogralApiDeparture[]; fetchedAt: number }>();
const CACHE_TTL = 5 * 60 * 1000;

export async function fetchDeparturesJson(agencyId: number, fresh = false): Promise<SogralApiDeparture[]> {
  const cached = departureCache.get(agencyId);
  if (!fresh && cached && Date.now() - cached.fetchedAt < CACHE_TTL) {
    return cached.data;
  }

  try {
    const res = await fetch(`${API_BASE}/departures/${agencyId}`);
    if (!res.ok) throw new Error(`SOGRAL JSON API failed: ${res.status}`);
    const data: SogralApiDeparture[] = await res.json();
    departureCache.set(agencyId, { data, fetchedAt: Date.now() });
    lsSet(`idanroh_departures_${agencyId}`, data);
    return data;
  } catch (err) {
    const persisted = lsGet<SogralApiDeparture[]>(`idanroh_departures_${agencyId}`);
    if (persisted) return persisted.data;
    throw err;
  }
}

export function getDeparturesCachedAt(agencyId: number): number | null {
  const persisted = lsGet<SogralApiDeparture[]>(`idanroh_departures_${agencyId}`);
  return persisted?.savedAt ?? null;
}

// P2 contains the scheduled departure time (clean — "04:15", "16:30") which
// is what Mahatati shows. S8 contains the actual departure timestamp (random
// minutes — "05:53", "16:37") because it gets overwritten when the bus leaves.
// Prefer P2; fall back to S8 only when P2 has no time (older entries with
// "T00:00:00").
export function getScheduledTime(d: SogralApiDeparture): string {
  const p2Time = d.P2?.split('T')[1]?.substring(0, 5);
  if (p2Time && p2Time !== '00:00') return p2Time;
  return d.S8?.split('T')[1]?.substring(0, 5) ?? '';
}

export function apiToSogralDeparture(d: SogralApiDeparture): SogralDeparture {
  const time = getScheduledTime(d);
  const seatsTotal = d.P11 ?? 0;
  const seatsSold = typeof d.P12 === 'number' ? Math.max(0, d.P12) : 0;
  const seatsAvailable = Math.max(0, seatsTotal - seatsSold);
  const occupancyRate = seatsTotal > 0 ? Math.min(1, seatsSold / seatsTotal) : 0;

  let status: SogralDeparture['status'] = 'ouvert';
  if (d.P8 === 2) status = 'assure';
  else if (d.P8 === 3) status = 'annule';

  return {
    time,
    destination: d.S6 || d.S12 || '',
    transporterName: (d.P10 || '').trim(),
    seatsAvailable,
    seatsTotal,
    occupancyRate,
    status,
  };
}

// ---------- Route stops (intermediate stops + stop-specific prices) ----------
// GET /api/live/departures/infos/route/{agencyId}/{destinationCode}/{routeId}
// Returns ordered list of stops: commune code, name, wilaya, price (S6), distance (P4)
// Cache key = routeId (route doesn't change; agencyId+destCode are route identifiers)

const routeStopsCache = new Map<number, SogralRouteStop[]>();

export async function fetchRouteStops(
  agencyId: number,
  destinationCode: string,
  routeId: number,
): Promise<SogralRouteStop[]> {
  const cached = routeStopsCache.get(routeId);
  if (cached) return cached;

  const res = await fetch(`${API_BASE}/departures/infos/route/${agencyId}/${destinationCode}/${routeId}`);
  if (!res.ok) throw new Error(`SOGRAL route stops failed: ${res.status}`);
  const data: SogralRouteStop[] = await res.json();
  routeStopsCache.set(routeId, data);
  return data;
}

// ---------- REST API endpoints ----------

export async function fetchSummary(agencyId?: number): Promise<SogralSummary> {
  const url = agencyId ? `${API_BASE}/summary/${agencyId}` : `${API_BASE}/summary`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`SOGRAL summary failed: ${res.status}`);
  return res.json();
}

export async function fetchDestinations(agencyId: number): Promise<SogralDestination[]> {
  // agencyId 0 = aggregate of both Alger stations
  if (agencyId === 0) {
    const seen = new Set<string>();
    const merged: SogralDestination[] = [];
    for (const id of [1, 2]) {
      try {
        const dests = await fetchDestinations(id);
        for (const d of dests) {
          if (!seen.has(d.P1)) {
            seen.add(d.P1);
            merged.push(d);
          }
        }
      } catch { /* skip failed agency */ }
    }
    return merged;
  }

  const url = `${API_BASE}/destinations/${agencyId}`;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`SOGRAL destinations failed: ${res.status}`);
    const data: SogralDestination[] = await res.json();
    const filtered = data.filter((d) => d.P2 && d.P2.trim() !== '');
    lsSet(`idanroh_destinations_${agencyId}`, filtered);
    return filtered;
  } catch (err) {
    const persisted = lsGet<SogralDestination[]>(`idanroh_destinations_${agencyId}`);
    if (persisted) return persisted.data;
    throw err;
  }
}

export async function fetchAllDestinations(): Promise<SogralDestination[]> {
  const allDests: SogralDestination[] = [];
  const seen = new Set<string>();

  for (let agencyId = 1; agencyId <= 5; agencyId++) {
    try {
      const dests = await fetchDestinations(agencyId);
      for (const d of dests) {
        if (!seen.has(d.P1)) {
          seen.add(d.P1);
          allDests.push(d);
        }
      }
    } catch {
      // Some agencies may not exist
    }
  }

  return allDests.sort((a, b) => a.P2.localeCompare(b.P2));
}

// ---------- Departure page scraping ----------

export function formatDateForSogral(date: Date): string {
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = String(date.getFullYear());
  return `${dd}${mm}${yyyy}`;
}

function sogralDateToISO(ddmmyyyy: string): string {
  const dd = ddmmyyyy.substring(0, 2);
  const mm = ddmmyyyy.substring(2, 4);
  const yyyy = ddmmyyyy.substring(4, 8);
  return `${yyyy}-${mm}-${dd}`;
}

export async function fetchDepartures(params: SogralSearchParams): Promise<SogralDeparture[]> {
  // Try JSON API first (has transporter names for historical data)
  try {
    const allDeps = await fetchDeparturesJson(params.agencyId);
    const targetDate = sogralDateToISO(params.date);

    // Match by wilaya (Algerian codes only: "213-...").
    // Non-Algerian destinations fall through to exact match only.
    const isAlgerian = params.destinationId.startsWith('213-');
    const wilayaSuffix = isAlgerian ? params.destinationId.split('-')[1] : undefined;
    const wilaya = wilayaSuffix ? Math.floor(parseInt(wilayaSuffix, 10) / 1000) : NaN;

    const filtered = allDeps
      .filter((d) => {
        if (d.S8?.split('T')[0] !== targetDate) return false;
        if (!Number.isNaN(wilaya) && Number(d.S7) === wilaya) return true;
        return d.S11 === params.destinationId || d.P4 === params.destinationId;
      })
      .map(apiToSogralDeparture);

    if (filtered.length > 0) return filtered;
  } catch {
    // Fall through to HTML scraping
  }

  // Fallback: HTML scraping
  const url = `${SCRAPE_BASE}?AgencyId=${params.agencyId}&DestinationId=${params.destinationId}&Date=${params.date}&Periode=${params.periode}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`SOGRAL page fetch failed: ${res.status}`);
  const html = await res.text();
  return parseDeparturesFromHtml(html);
}

export function parseDeparturesFromHtml(html: string): SogralDeparture[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const table = doc.querySelector('#DeparturesTable');
  if (!table) return [];

  const rows = table.querySelectorAll('tbody tr');
  const departures: SogralDeparture[] = [];

  for (const row of rows) {
    const cells = row.querySelectorAll('td');
    if (cells.length < 7) continue;

    try {
      // Columns: [0] dt-control, [1] Tête de ligne, [2] Transporteur, [3] Date/Heure, [4] N° Zone, [5] Disponible/Total, [6] Etat
      const dateTimeCell = cells[3]?.textContent?.trim() ?? '';
      const seatsCell = cells[5]?.textContent?.trim() ?? '';
      const statusCell = cells[6]?.textContent?.trim().toLowerCase() ?? '';

      const timeMatch = dateTimeCell.match(/(\d{2}):(\d{2})/);
      if (!timeMatch) continue;
      const time = `${timeMatch[1]}:${timeMatch[2]}`;

      const destination = cells[1]?.textContent?.trim() ?? '';
      const transporterName = cells[2]?.textContent?.trim() ?? '';

      // Parse seats: "27 / 50" or "0 / 50"
      const seatsMatch = seatsCell.match(/(\d+)\s*\/\s*(\d+)/);
      if (!seatsMatch) continue;

      const seatsAvailable = parseInt(seatsMatch[1], 10);
      const seatsTotal = parseInt(seatsMatch[2], 10);
      const occupancyRate = seatsTotal > 0 ? (seatsTotal - seatsAvailable) / seatsTotal : 0;

      // Parse status
      let status: SogralDeparture['status'] = 'ouvert';
      if (statusCell.includes('assur')) status = 'assure';
      else if (statusCell.includes('annul')) status = 'annule';

      departures.push({
        time,
        destination,
        transporterName,
        seatsAvailable,
        seatsTotal,
        occupancyRate,
        status,
      });
    } catch {
      // Skip unparseable rows
    }
  }

  // Sort by time
  departures.sort((a, b) => a.time.localeCompare(b.time));

  return departures;
}
